import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useStores, type SearchFilters } from "../stores";
import type { ImovelTipo } from "../scripts/db";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PrimaryButton from "../components/PrimaryButton";
import BreadcrumbNav from "../components/Breadcrumb";
import PropertyCard from "../components/PropertyCard";
import PropertyCardSkeleton from "../components/PropertyCardSkeleton";

const TIPO_TO_SINGULAR: Record<string, ImovelTipo> = {
  casas: "casa",
  apartamentos: "apto",
  lotes: "lote",
  lojas: "loja",
  salas: "sala",
  outros: "outros",
};

const PAGE_CONTENT: Record<string, { title: string; description: string }> = {
  casas: {
    title: "Casas à Venda",
    description:
      "Explore nossa seleção exclusiva de residências nos melhores bairros.",
  },
  apartamentos: {
    title: "Apartamentos à Venda",
    description: "Apartamentos nos melhores bairros da cidade.",
  },
  lotes: {
    title: "Lotes à Venda",
    description:
      "Terrenos com localização privilegiada para você construir o imóvel dos sonhos.",
  },
  lojas: {
    title: "Lojas à Venda",
    description: "Pontos comerciais estratégicos para alavancar seu negócio.",
  },
  salas: {
    title: "Salas Comerciais à Venda",
    description: "Salas comerciais modernas em endereços corporativos.",
  },
  outros: {
    title: "Outros Imóveis",
    description: "Propriedades especiais que fogem do convencional.",
  },
};

const Imoveis: React.FC = () => {
  const { tipo } = useParams<{ tipo: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { filterStore } = useStores();
  const {
    results,
    totalCount,
    currentPage,
    pageSize,
    isLoading,
    error,
    neighborhoods: bairros,
    cities,
  } = filterStore;

  const content = PAGE_CONTENT[tipo || "casas"] || PAGE_CONTENT.casas;

  const [selectedCity, setSelectedCity] = useState("Todas as Cidades");
  const [selectedNeighborhood, setSelectedNeighborhood] =
    useState("Todos os Bairros");
  const [bedrooms, setBedrooms] = useState("1+");
  const [bathrooms, setBathrooms] = useState("1+");
  const [parking, setParking] = useState("1+");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const readFiltersFromURL = useCallback(() => {
    const q = searchParams.get("quartos");
    if (q) setBedrooms(`${q}+`);
    const b = searchParams.get("banheiros");
    if (b) setBathrooms(`${b}+`);
    const v = searchParams.get("vagas");
    if (v) setParking(`${v}+`);
    const pmin = searchParams.get("pmin");
    if (pmin) setMinPrice(pmin);
    const pmax = searchParams.get("pmax");
    if (pmax) setMaxPrice(pmax);
    const cidade = searchParams.get("cidade");
    if (cidade) setSelectedCity(cidade);
    const bairro = searchParams.get("bairro");
    if (bairro) setSelectedNeighborhood(bairro);
    const sort = searchParams.get("sort");
    if (sort) setSortBy(sort);
    const page = searchParams.get("page");
    if (page) filterStore.currentPage = Number(page);
  }, [searchParams]);

  useEffect(() => {
    filterStore.loadNeighborhoods();
    filterStore.loadCities();
    readFiltersFromURL();
    const filters: SearchFilters = {};
    if (tipo && TIPO_TO_SINGULAR[tipo]) filters.type = TIPO_TO_SINGULAR[tipo];
    const q = searchParams.get("quartos");
    if (q) filters.bedrooms = Number(q);
    const b = searchParams.get("banheiros");
    if (b) filters.bathrooms = Number(b);
    const v = searchParams.get("vagas");
    if (v) filters.parkingSpots = Number(v);
    const pmin = searchParams.get("pmin");
    if (pmin) filters.minPrice = Number(pmin);
    const pmax = searchParams.get("pmax");
    if (pmax) filters.maxPrice = Number(pmax);
    const cidade = searchParams.get("cidade");
    if (cidade) filters.city = cidade;
    const bairro = searchParams.get("bairro");
    if (bairro) filters.neighborhood = bairro;
    const sort = searchParams.get("sort");
    if (sort) (filters as any).sort = sort;
    filterStore.setFilters(filters);
    filterStore.search();
    return () => {
      filterStore.resetFilters();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);

  const buildFilters = (): SearchFilters => {
    const filters: SearchFilters = {};
    if (tipo && TIPO_TO_SINGULAR[tipo]) filters.type = TIPO_TO_SINGULAR[tipo];
    const rooms = bedrooms.replace("+", "");
    if (rooms !== "1") filters.bedrooms = Number(rooms);
    const baths = bathrooms.replace("+", "");
    if (baths !== "1") filters.bathrooms = Number(baths);
    const park = parking.replace("+", "");
    if (park !== "1") filters.parkingSpots = Number(park);
    if (minPrice) filters.minPrice = Number(minPrice);
    if (maxPrice) filters.maxPrice = Number(maxPrice);
    if (selectedCity && selectedCity !== "Todas as Cidades")
      filters.city = selectedCity;
    if (selectedNeighborhood && selectedNeighborhood !== "Todos os Bairros")
      filters.neighborhood = selectedNeighborhood;
    if (sortBy !== "newest") (filters as any).sort = sortBy;
    return filters;
  };

  const handleSearch = () => {
    const filters = buildFilters();
    filterStore.setFilters(filters);
    filterStore.currentPage = 1;
    filterStore.search();
    setShowMobileFilters(false);
  };

  const handleClearFilters = () => {
    setSelectedCity("Todas as Cidades");
    setSelectedNeighborhood("Todos os Bairros");
    setBedrooms("1+");
    setBathrooms("1+");
    setParking("1+");
    setMinPrice("");
    setMaxPrice("");
    setSortBy("newest");
    filterStore.resetFilters();
    const filters: SearchFilters = {};
    if (tipo && TIPO_TO_SINGULAR[tipo]) filters.type = TIPO_TO_SINGULAR[tipo];
    filterStore.setFilters(filters);
    filterStore.search();
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    const filters = buildFilters();
    if (value === "newest") {
      delete (filters as any).sort;
    } else {
      (filters as any).sort = value;
    }
    filterStore.setFilters(filters);
    filterStore.currentPage = 1;
    filterStore.search();
  };

  const handlePageChange = (page: number) => {
    const next = new URLSearchParams(searchParams);
    if (page > 1) {
      next.set("page", String(page));
    } else {
      next.delete("page");
    }
    setSearchParams(next, { replace: true });
    filterStore.loadPage(page);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      ) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }

    return (
      <nav className="mt-5 d-flex justify-content-center">
        <ul className="pagination shadow-sm rounded-3 overflow-hidden border-0 gap-1 mb-0">
          <li className={`page-item ${currentPage <= 1 ? "disabled" : ""}`}>
            <button
              className="bg-white text-muted px-3 py-2 border rounded-2"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              <i className="fas fa-chevron-left small"></i>
            </button>
          </li>
          {pages.map((p, idx) =>
            typeof p === "string" ? (
              <li key={`ellipsis-${idx}`} className="page-item disabled">
                <span className="bg-white text-muted px-2 py-2 d-inline-block">
                  ...
                </span>
              </li>
            ) : (
              <li key={p} className="page-item">
                <button
                  className={`px-3 py-2 border rounded-2 d-inline-block ${p === currentPage ? "text-white fw-bold" : "bg-white text-dark"}`}
                  style={
                    p === currentPage
                      ? { backgroundColor: "#0B2545", borderColor: "#0B2545" }
                      : {}
                  }
                  onClick={() => handlePageChange(p)}
                >
                  {p}
                </button>
              </li>
            ),
          )}
          <li
            className={`page-item ${currentPage >= totalPages ? "disabled" : ""}`}
          >
            <button
              className="bg-white text-dark px-3 py-2 border rounded-2 d-inline-block"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              <i className="fas fa-chevron-right small"></i>
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  return (
    <div>
      <Header />
      <div className="bg-light pb-5">
        <div className="container pt-4">
          <BreadcrumbNav
            items={[{ label: "Home", href: "/" }, { label: content.title }]}
          />
        </div>

        <header className="container mb-4">
          <h1 className="fw-bold display-5 mb-2" style={{ color: "#0B2545" }}>
            {content.title}
          </h1>
          <p className="text-muted col-lg-7 fs-6 lh-base">
            {content.description}
          </p>
        </header>

        <div className="container">
          <div className="d-block d-lg-none mb-3">
            <button
              className="btn bg-white border w-100 d-flex align-items-center justify-content-center gap-2 fw-semibold text-muted shadow-sm py-2"
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <i className="fas fa-sliders-h" style={{ color: "#0B2545" }}></i>
              {showMobileFilters ? "Ocultar Filtros" : "Filtrar Resultados"}
            </button>
          </div>

          <div className="row g-4">
            <div
              className={`col-lg-3 ${showMobileFilters ? "d-block" : "d-none d-lg-block"}`}
            >
              <div
                className="bg-white rounded-3 border p-4 shadow-sm sticky-top"
                style={{
                  top: "100px",
                  zIndex: 10,
                  maxHeight: "calc(100vh - 120px)",
                  overflowY: "auto",
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <h5 className="fw-bold m-0" style={{ color: "#0B2545" }}>
                    Filtros
                  </h5>
                  <button
                    onClick={handleClearFilters}
                    className="btn btn-link text-decoration-none text-success small fw-semibold p-0"
                  >
                    Limpar
                  </button>
                </div>

                <div className="mb-4">
                  <label className="form-label small fw-bold text-muted mb-2">
                    Cidade
                  </label>
                  <select
                    className="form-select bg-light border-0 py-2 text-secondary fw-medium"
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                  >
                    <option value="Todas as Cidades">Todas as Cidades</option>
                    {cities.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="form-label small fw-bold text-muted mb-2">
                    Bairro
                  </label>
                  <select
                    className="form-select bg-light border-0 py-2 text-secondary fw-medium"
                    value={selectedNeighborhood}
                    onChange={(e) => setSelectedNeighborhood(e.target.value)}
                  >
                    <option value="Todos os Bairros">Todos os Bairros</option>
                    {bairros.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="form-label small fw-bold text-muted mb-2">
                    Quartos
                  </label>
                  <div className="d-flex gap-2">
                    {["1+", "2+", "3+", "4+"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setBedrooms(opt)}
                        className={`btn flex-fill py-2 fw-bold small rounded-2 ${bedrooms === opt ? "text-white" : "btn-light text-dark"}`}
                        style={
                          bedrooms === opt ? { backgroundColor: "#0B2545" } : {}
                        }
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label small fw-bold text-muted mb-2">
                    Banheiros
                  </label>
                  <div className="d-flex gap-2">
                    {["1+", "2+", "3+", "4+"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setBathrooms(opt)}
                        className={`btn flex-fill py-2 fw-bold small rounded-2 ${bathrooms === opt ? "text-white" : "btn-light text-dark"}`}
                        style={
                          bathrooms === opt
                            ? { backgroundColor: "#0B2545" }
                            : {}
                        }
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label small fw-bold text-muted mb-2">
                    Vagas
                  </label>
                  <div className="d-flex gap-2">
                    {["1+", "2+", "3+", "4+"].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setParking(opt)}
                        className={`btn flex-fill py-2 fw-bold small rounded-2 ${parking === opt ? "text-white" : "btn-light text-dark"}`}
                        style={
                          parking === opt ? { backgroundColor: "#0B2545" } : {}
                        }
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label small fw-bold text-muted mb-2">
                    Faixa de Preço
                  </label>
                  <input
                    type="text"
                    className="form-control bg-light border-0 py-2 mb-2 text-muted small"
                    placeholder="Mín: R$ 0"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                  />
                  <input
                    type="text"
                    className="form-control bg-light border-0 py-2 text-muted small"
                    placeholder="Máx: Sem Limite"
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                  />
                </div>

                <PrimaryButton
                  variant="primary"
                  fullWidth
                  onClick={handleSearch}
                  icon="fas fa-search"
                >
                  Pesquisar
                </PrimaryButton>
              </div>
            </div>

            <div className="col-lg-9">
              <div className="bg-white rounded-3 border px-4 py-3 mb-4 d-flex align-items-center justify-content-between shadow-sm flex-wrap gap-3">
                <span className="text-muted small fw-medium">
                  {isLoading ? (
                    "Buscando..."
                  ) : (
                    <>
                      Mostrando{" "}
                      <strong className="text-dark">{totalCount}</strong>{" "}
                      {`imóve${totalCount > 1 ? "is" : "l"} encontrado${totalCount > 1 ? "s" : ""}`}
                    </>
                  )}
                </span>
                <div className="d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <label className="text-muted small text-nowrap m-0">
                      Ordenar por:
                    </label>
                    <select
                      className="form-select form-select-sm border-0 bg-transparent fw-bold text-dark ps-1 pe-4 py-1"
                      value={sortBy}
                      onChange={(e) => handleSortChange(e.target.value)}
                      style={{ width: "auto", cursor: "pointer" }}
                    >
                      <option value="newest">Mais recentes</option>
                      <option value="price_asc">Menor Preço</option>
                      <option value="price_desc">Maior Preço</option>
                    </select>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <label className="text-muted small text-nowrap m-0">
                      Exibir:
                    </label>
                    <select
                      className="form-select form-select-sm border-0 bg-transparent fw-bold text-dark ps-1 pe-4 py-1"
                      value={pageSize}
                      onChange={(e) =>
                        filterStore.setPageSize(Number(e.target.value))
                      }
                      style={{ width: "auto", cursor: "pointer" }}
                    >
                      <option value={12}>12</option>
                      <option value={24}>24</option>
                      <option value={48}>48</option>
                    </select>
                  </div>
                  <div className="d-none d-md-flex border rounded-2 overflow-hidden">
                    <button
                      className="btn btn-sm border-0 px-2 py-1"
                      style={{ backgroundColor: "#EEF4F8" }}
                    >
                      <i
                        className="fas fa-th-large"
                        style={{ color: "#0B2545" }}
                      ></i>
                    </button>
                    <button className="btn btn-sm btn-white border-0 px-2 py-1">
                      <i className="fas fa-list text-muted"></i>
                    </button>
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="row g-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="col-12 col-md-6 col-xl-4">
                      <PropertyCardSkeleton />
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="text-center py-5">
                  <i className="fas fa-exclamation-triangle display-4 text-danger mb-3"></i>
                  <h4 className="fw-bold" style={{ color: "#0B2545" }}>
                    Erro ao carregar imóveis
                  </h4>
                  <p className="text-muted">{error}</p>
                </div>
              ) : results.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-search-minus display-4 text-muted mb-3"></i>
                  <h4 className="fw-bold" style={{ color: "#0B2545" }}>
                    Infelizmente não encontramos nada aqui
                  </h4>
                  <p className="text-muted">
                    Tente ajustar os filtros ou buscar por outra categoria.
                  </p>
                </div>
              ) : (
                <>
                  <div className="row g-4">
                    {results.map((imovel) => (
                      <div
                        key={imovel.code}
                        className="col-12 col-md-6 col-xl-4"
                      >
                        <PropertyCard property={imovel} />
                      </div>
                    ))}
                  </div>
                  {renderPagination()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default observer(Imoveis);
